import * as React from "react";
import { Fragment, useMemo } from "react";
import { FaPlus } from "react-icons/fa";
import { Badge, Button, Col, Form, Image } from "react-bootstrap";
import { CrossSellModel } from "../../../models/domain/crossSellModel";
import { useBundleForm } from "../../../hooks/useBundleForm";
import { FormProvider } from "react-hook-form";
import { useAppContext } from "../../../hooks/useAppContext";
import { BundleModel } from "../../../models/view/bundleModel";
import { pushToDataLayer } from "../../../hooks/useCrossSellDataLayerPush";

export const Bundle = ({ model: { crossSells } }: {
    model: BundleModel
}) => {
    const {
        form,
        onSubmit,
        selected: {
            ids,
            crossSells: selectedCrossSells,
            price
        },
        addToCartButtonText,
        zeroBundledItems
    } = useBundleForm(crossSells);

    // Initialize an empty products array
    let products: Array<{
        title: string;
        sku: string;
        price: number;
        listType: string;
    }> = [];

    // If there are cross-sells, populate the products array
    if (!zeroBundledItems) {
        products = selectedCrossSells.map((model) => ({
            title: model.product.title,
            sku: model.product.sku,
            price: model.price.unitPrice,
            listType: 'ProductNew | Bundle',
        }));

         pushToDataLayer(products);
    }

    // If there are no cross-sells (excluding the current product), don't render anything
    if (zeroBundledItems) return <></>;

    return (
        <Col>
            <div className="border rounded p-3">
                <span className="fw-normal fs-4 pb-2">Make it a Bundle!</span>
                <div id="bundleSection">
                    <div className="d-flex flex-wrap align-items-center mb-3">
                        <div className="d-flex align-items-center flex-wrap">
                            {selectedCrossSells.map(({
                                product: { title, sku, productId, primaryId, path },
                                image: { src }
                            }, index) => (
                                <Fragment key={index}>
                                    <div className="py-3">
                                        {index > 0 && <FaPlus className="mx-2" aria-hidden />}
                                        <Thumbnail key={index}
                                            src={src}
                                            title={title}
                                            productId={productId}
                                            primaryId={primaryId}
                                            sku={sku}
                                            path={path}
                                            index={index} />
                                    </div>
                                </Fragment>
                            ))}
                            <div className="d-flex flex-column ms-3 mt-3 mt-sm-0 ">
                                {ids.length > 0 && (
                                    <>
                                        <div className="fs-6">Total Price:{' '}
                                            <span className="fs-5  fw-bold">
                                                {price}
                                            </span>
                                        </div>
                                        <Button
                                            variant='warning'
                                            size='sm'
                                            className='add-to-cart-btn'
                                            data-bundle="true"
                                            data-ee-list='ProductNew | Bundle'
                                            type='submit'
                                            form='bundle-form'
                                        >
                                            {addToCartButtonText}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
                <div className="w-100">
                    <FormProvider {...form}>
                        <Form id="bundle-form" onSubmit={onSubmit}>
                            {crossSells.map((crossSell) => (
                                <Form.Check
                                    {...form.register("items")}
                                    key={crossSell.product.productId}
                                    id={`bundle-${crossSell.product.productId}`}
                                    data-sku={crossSell.product.sku}
                                    data-quantity={crossSell.product.minQuantity}
                                    data-ee-list='ProductNew | Bundle'
                                    checked={ids.some((selectedProductId: number) => selectedProductId === crossSell.product.productId)}
                                    onChange={({ target: { checked } }) => form.setValue('items', checked ? [...ids, crossSell.product.productId] : ids.filter((formItem: number) => formItem !== crossSell.product.productId))}
                                    type="checkbox"
                                    label={<Label model={crossSell} />}
                                />
                            ))}
                        </Form>
                    </FormProvider>
                </div>
            </div>
        </Col>
    )
}

const Thumbnail = ({ index, path, primaryId, productId, sku, src, title }: {
    index: number,
    path: string,
    primaryId?: number,
    productId: number,
    sku: string,
    src: string,
    title?: string
}) => {
    const { changeProduct } = useAppContext();
    const isActiveProduct = useMemo(() => index === 0, [index]);
    return (
        <Button
            variant="link"
            href={isActiveProduct ? undefined : path}
            aria-label="View product details"
            data-ee-list='ProductNew | Bundle'
            data-react="true"
            data-item-id={productId}
            data-position={index}
            data-sku={sku}
            className={`product-link  ${isActiveProduct && 'active-product-link'}`}
            onClick={(e) => {
                // Prevents the link
                // from being followed
                // to allow the request
                // to be made locally without a page refresh
                e.preventDefault();

                // Prevents the link from being followed to the current page
                if (isActiveProduct) return;

                changeProduct(productId, primaryId, path);
            }}
        >
            <div className="img-thumbnail position-relative">
                {isActiveProduct &&
                    <Badge bg="success" className="position-absolute top-0 start-0 rounded-0">This Product</Badge>}
                <Image className="image-fluid"
                    src={src}
                    alt={title}
                    title={title}
                    height={200}
                    width={155} />
            </div>
        </Button>
    );
};

const Label = ({
    model: {
        product: {
            productId: itemId,
            title,
        },
        price: {
            price,
            unitPrice,
            onSale,
            salePercentage
        }
    }
}: {
    model: CrossSellModel
}) => {
    const { productId } = useAppContext();

    return (
        <div className="dont-break-out">

            {itemId === productId && <strong className="me-1">This Product:</strong>}
            {title}
            {onSale && <del className="ms-2 fw-normal text-muted fw-normal me-1">
                <small>${unitPrice}</small>
            </del>}
            <span className={`fw-bold text-dark ms-1`}>
                ${price}
            </span>

            {onSale && (
                <span className="text-danger fw-normal px-1">Save {salePercentage && Math.round(salePercentage)}%</span>
            )}

        </div>
    );
};